import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trash2, UserPlus, Building2, Printer, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { preparePrint } from "@/lib/printLabel";
import { ROLE_LABELS } from "@/lib/roles";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [settings, setSettings] = useState({ tare: 79, seuil: 5, print_mode: "auto", copies: 1 });

  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "operateur" });
  const [newRec, setNewRec] = useState({ nom: "", adresse: "", cp_ville: "", type_accepte: "P" });

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/recipients").then((r) => setRecipients(r.data));
    api.get("/settings").then((r) => setSettings({
      tare: r.data.tare, seuil: r.data.seuil, print_mode: r.data.print_mode, copies: r.data.copies,
    }));
  };
  useEffect(load, []);

  const testLabel = async () => {
    const job = preparePrint(settings);
    try {
      const r = await api.post("/settings/test-etiquette");
      job.print(r.data.pdf_url);
      toast.success(job.channel === "brother" ? "Étiquette de test envoyée à iPrint&Label" : "Étiquette de test ouverte (PDF)");
    } catch (e) { job.cancel(); toast.error(apiError(e.response?.data?.detail)); }
  };

  const createUser = async () => {
    try {
      await api.post("/users", newUser);
      toast.success("Utilisateur créé");
      setNewUser({ name: "", email: "", password: "", role: "operateur" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createRec = async () => {
    try {
      await api.post("/recipients", newRec);
      toast.success("Destinataire créé");
      setNewRec({ nom: "", adresse: "", cp_ville: "", type_accepte: "P" });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/settings", {
        tare: parseFloat(settings.tare), seuil: parseFloat(settings.seuil),
        print_mode: settings.print_mode, copies: parseInt(settings.copies, 10) || 1,
      });
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="admin">
      <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900">
        Paramètres exploitation
      </h1>

      <Tabs defaultValue="users">
        <TabsList className="grid grid-cols-3 h-12">
          <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="recipients" data-testid="admin-tab-recipients">Destinataires</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Réglages</TabsTrigger>
        </TabsList>

        {/* USERS */}
        <TabsContent value="users" className="space-y-4 mt-4">
          <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 grid sm:grid-cols-2 gap-3">
            <Input placeholder="Nom" data-testid="user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="h-11" />
            <Input placeholder="Email" data-testid="user-email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="h-11" />
            <Input placeholder="Mot de passe" type="password" data-testid="user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="h-11" />
            <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
              <SelectTrigger data-testid="user-role" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="operateur">Opérateur</SelectItem>
                <SelectItem value="responsable">Responsable conditionnement</SelectItem>
                <SelectItem value="chauffeur">Chauffeur</SelectItem>
                <SelectItem value="admin">Administrateur</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="user-create-btn" onClick={createUser} className="sm:col-span-2 h-11 bg-emerald-600 hover:bg-emerald-700 font-bold">
              <UserPlus className="w-5 h-5 mr-1" /> Ajouter l'utilisateur
            </Button>
          </div>
          <div className="rounded-2xl bg-white border-2 border-slate-200 divide-y">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-4">
                <div>
                  <div className="font-bold text-slate-900">{u.name} <span className="text-xs uppercase text-slate-400 ml-1">{ROLE_LABELS[u.role] || u.role}</span></div>
                  <div className="text-sm text-slate-500">{u.email}</div>
                </div>
                <Button variant="ghost" size="icon" className="text-rose-500" data-testid={`user-delete-${u.email}`}
                  onClick={async () => { try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }}>
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* RECIPIENTS */}
        <TabsContent value="recipients" className="space-y-4 mt-4">
          <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 grid sm:grid-cols-2 gap-3">
            <Input placeholder="Nom" data-testid="rec-nom" value={newRec.nom} onChange={(e) => setNewRec({ ...newRec, nom: e.target.value })} className="h-11" />
            <Select value={newRec.type_accepte} onValueChange={(v) => setNewRec({ ...newRec, type_accepte: v })}>
              <SelectTrigger data-testid="rec-type" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="P">Conforme (P)</SelectItem>
                <SelectItem value="DECLASSEE">Déclassé</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Adresse" data-testid="rec-adresse" value={newRec.adresse} onChange={(e) => setNewRec({ ...newRec, adresse: e.target.value })} className="h-11" />
            <Input placeholder="Code postal & ville" data-testid="rec-cp" value={newRec.cp_ville} onChange={(e) => setNewRec({ ...newRec, cp_ville: e.target.value })} className="h-11" />
            <Button data-testid="rec-create-btn" onClick={createRec} className="sm:col-span-2 h-11 bg-emerald-600 hover:bg-emerald-700 font-bold">
              <Building2 className="w-5 h-5 mr-1" /> Ajouter le destinataire
            </Button>
          </div>
          <div className="rounded-2xl bg-white border-2 border-slate-200 divide-y">
            {recipients.map((r) => (
              <div key={r.id} className="flex items-center justify-between p-4">
                <div>
                  <div className="font-bold text-slate-900">{r.nom}
                    <span className={`text-xs uppercase ml-2 px-2 py-0.5 rounded ${r.type_accepte === "P" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {r.type_accepte === "P" ? "Conforme" : "Déclassé"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500">{r.adresse}, {r.cp_ville}</div>
                </div>
                <Button variant="ghost" size="icon" className="text-rose-500" data-testid={`rec-delete-${r.nom}`}
                  onClick={async () => { await api.delete(`/recipients/${r.id}`); load(); }}>
                  <Trash2 className="w-5 h-5" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="mt-4 space-y-4">
          <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Tare standard (kg)</Label>
              <Input data-testid="setting-tare" type="number" step="0.1" value={settings.tare} onChange={(e) => setSettings({ ...settings, tare: e.target.value })} className="mt-1 h-12" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Seuil d'alerte déclassement (%)</Label>
              <Input data-testid="setting-seuil" type="number" step="0.1" value={settings.seuil} onChange={(e) => setSettings({ ...settings, seuil: e.target.value })} className="mt-1 h-12" />
            </div>
          </div>

          <div className="rounded-2xl bg-white border-2 border-slate-200 p-5 space-y-4" data-testid="print-settings-block">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-heading font-black text-lg uppercase tracking-tight text-slate-900 flex items-center gap-2">
                  <Printer className="w-5 h-5" /> Impression étiquettes
                </div>
                <div className="text-xs text-slate-500 font-medium">Brother QL-820NWBc · rouleau DK-11202 · 62 × 100 mm</div>
              </div>
              <Link to="/aide/imprimante" data-testid="print-help-link" className="text-sm font-bold text-slate-700 underline flex items-center gap-1">
                <HelpCircle className="w-4 h-4" /> Configuration
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Mode impression</Label>
                <Select value={settings.print_mode} onValueChange={(v) => setSettings({ ...settings, print_mode: v })}>
                  <SelectTrigger data-testid="setting-print-mode" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (défaut)</SelectItem>
                    <SelectItem value="iprint">Toujours iPrint&Label</SelectItem>
                    <SelectItem value="system">Toujours boîte système</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide font-bold text-slate-500">Copies par palette (1 à 3)</Label>
                <Input data-testid="setting-copies" type="number" min={1} max={3} step={1} value={settings.copies}
                  onChange={(e) => setSettings({ ...settings, copies: e.target.value })} className="mt-1 h-12" />
              </div>
            </div>
            <Button data-testid="print-test-btn" variant="outline" onClick={testLabel} className="h-12 font-bold w-full sm:w-auto">
              <Printer className="w-5 h-5 mr-1" /> Imprimer une étiquette de test
            </Button>
          </div>

          <Button data-testid="settings-save-btn" onClick={saveSettings} className="w-full h-12 bg-slate-900 hover:bg-slate-800 font-bold">
            Enregistrer les paramètres
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
